import { pdf } from "@react-pdf/renderer";
import { TimesheetReport } from "domain/entities/timesheet-report";
import { BaseInputFilter } from "domain/models/base-input-filter";
import { DocumentInputFilter } from "domain/models/document-input-filter";
import { DocumentTypeInputFilter } from "domain/models/document-type-input-filter";
import { EmployeeInputFilter } from "domain/models/employee-input-filter";
import TimesheetReportPDF from "presentation/components/Timesheet/TimesheetReportPDF";
import { useDocumentByFilter } from "presentation/hooks/use-query-document";
import { useDocumentTypeByFilter } from "presentation/hooks/use-query-document-type";
import { useEmployeesByFilter } from "presentation/hooks/use-query-employees";
import { useTimesheetReportsAllEmployee } from "presentation/hooks/use-query-timesheet-report";
import { useTimesheetUploader } from "presentation/hooks/useTimesheetUploader";
import { Button } from "primereact/button";
import { ProgressBar } from "primereact/progressbar";
import { Toast } from "primereact/toast";
import { useEffect, useRef, useState } from "react";

// Componente interno com a lógica de negócio, garantindo que os hooks são chamados incondicionalmente.
const TimesheetReportUploader: React.FC<{
  date: Date;
  onComplete: () => void;
}> = ({ date, onComplete }) => {
  const toast = useRef<Toast>(null);
  const parsedYear = date.getFullYear();
  const parsedMonth = date.getMonth() + 1;

  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState("");

  const { processSingleReport, deleteDocumentMutation } =
    useTimesheetUploader();

  const [timesheetSentSatus, setTimesheetSentSatus] = useState<
    TimesheetReport[] | null | undefined
  >();

  const { data: timesheetData } = useTimesheetReportsAllEmployee(
    parsedMonth.toString(),
    parsedYear.toString(),
    {
      limit: 100,
    } as BaseInputFilter
  );

  const { data: employeesData } = useEmployeesByFilter({
    limit: 100,
  } as EmployeeInputFilter);

  const { data: docTypeData } = useDocumentTypeByFilter({
    limit: 1,
    name: "Mapa de Horas",
  } as DocumentTypeInputFilter);
  const docTypeId = docTypeData?.data?.[0]?.id;

  const monthYearString = `${String(parsedMonth).padStart(
    2,
    "0"
  )}-${parsedYear}`;
  const { data: existingDocuments } = useDocumentByFilter({
    fullPath: monthYearString,
    documentTypeId: docTypeId,
    source: "other",
    limit: 100,
  } as DocumentInputFilter);

  useEffect(() => {
    if (timesheetData?.data) {
      const filteredTimesheet = timesheetData.data.filter(
        (report) => report.status == "Enviado"
      );

      setTimesheetSentSatus(filteredTimesheet);
    }
  }, [timesheetData?.data]);

  const generateAndDownloadProcess = async () => {
    const reports = timesheetSentSatus;
    const employees = employeesData?.data;
    const existingDocs = existingDocuments?.data;

    if (!reports || reports.length === 0 || !employees) {
      toast.current?.show({
        severity: "warn",
        summary: "Aviso",
        detail: "Não há dados suficientes para gerar os relatórios.",
      });
      return;
    }

    setIsLoading(true);

    if (existingDocs && existingDocs.length > 0) {
      setProgressMessage(
        `Removendo ${existingDocs.length} relatórios antigos...`
      );
      await Promise.allSettled(
        existingDocs.map((doc) => deleteDocumentMutation(doc))
      );
    }

    let successCount = 0;
    const total = reports.length;
    const generatedPdfs: { blob: Blob; filename: string }[] = [];

    // Etapa 1: Gerar e fazer upload dos PDFs
    for (let i = 0; i < total; i++) {
      const report = reports[i];
      const employee = employees.find((e) => e.emailAddress === report.email);

      setProgressMessage(`Enviando ${i + 1}/${total}: ${report.name}`);

      if (!employee || report.email === "no@email.pt") continue;

      try {
        const blob = await pdf(
          <TimesheetReportPDF
            data={report}
            isSuportService={!!employee.isSuportService}
          />
        ).toBlob();

        const sanitizedName =
          employee.name?.replace(/[^a-zA-Z0-9]/g, "_") || "funcionario";
        const filename = `mapa_horas_${sanitizedName}_${monthYearString.replace(
          "-",
          "_"
        )}.pdf`;
        generatedPdfs.push({ blob, filename });

        await processSingleReport({
          blob,
          report,
          employee,
          docTypeId: docTypeId as string,
        });

        successCount++;
      } catch (error) {
        console.error(`Erro no processo para ${report.name}:`, error);
      }
      setProgress(((i + 1) / total) * 100);
    }

    // Etapa 2: Gerar e descarregar o ZIP a partir dos blobs em memória
    if (generatedPdfs.length > 0) {
      toast.current?.show({
        severity: "info",
        summary: "Upload Concluído",
        detail: "A gerar ficheiro ZIP...",
      });
      try {
        const JSZip = (await import("jszip")).default;
        const zip = new JSZip();

        generatedPdfs.forEach((pdfFile) =>
          zip.file(pdfFile.filename, pdfFile.blob)
        );

        const zipBlob = await zip.generateAsync({ type: "blob" });
        const url = URL.createObjectURL(zipBlob);
        const link = document.createElement("a");
        link.href = url;
        const timestamp = new Date()
          .toISOString()
          .slice(0, 10)
          .replace(/-/g, "_");
        link.download = `mapas_horas_${parsedYear}_${String(
          parsedMonth
        ).padStart(2, "0")}_${timestamp}.zip`;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (error) {
        const msg =
          error instanceof Error ? error.message : "Erro desconhecido";
        toast.current?.show({
          severity: "error",
          summary: "Erro no ZIP",
          detail: `Não foi possível gerar o ficheiro ZIP: ${msg}`,
          sticky: true,
        });
      }
    }

    setIsLoading(false);
    setProgress(0);
    setProgressMessage("");
  };

  return (
    <div>
      <Toast ref={toast} />
      <div className="default-grid header-spacing">
        <div className="default-2-cols">
          {
            <p className="p-0 m-0">
              Foram encontrados {timesheetSentSatus?.length ?? 0} relatórios de
              Mapa de Horas para o período {parsedMonth}/{parsedYear}.
            </p>
          }
        </div>
        <div className="default-2-cols button-align">
          <Button
            label="Voltar"
            icon="pi pi-arrow-left"
            onClick={onComplete}
            disabled={isLoading}
            className="p-button-secondary"
          />

          <Button
            label="Descarregar"
            icon="pi pi-download"
            onClick={generateAndDownloadProcess}
            loading={isLoading}
            disabled={!timesheetSentSatus || timesheetSentSatus.length === 0}
            className="mr-3"
          />
        </div>
      </div>

      {isLoading && (
        <div className="mt-4">
          <ProgressBar value={progress.toFixed(0)}></ProgressBar>
          {progressMessage && (
            <small className="p-text-secondary mt-1">{progressMessage}</small>
          )}
        </div>
      )}
    </div>
  );
};

interface TimesheetReportUploadProps {
  date: Date | null;
  onComplete: () => void;
}

/**
 * Componente que orquestra a geração, upload de relatórios de timesheet
 * e o download de um arquivo ZIP com os mesmos.
 */
const TimesheetReportUpload: React.FC<TimesheetReportUploadProps> = ({
  date,
  onComplete,
}) => {
  // Se a data for nula, exibe uma mensagem.
  // Isso evita a chamada condicional de hooks.
  if (!date) {
    return (
      <div className="flex flex-column align-items-center justify-content-center p-5 text-center">
        <i
          className="pi pi-calendar-times"
          style={{ fontSize: "3em", color: "var(--text-color-secondary)" }}
        ></i>
        <h3 className="mt-3 text-color-secondary">
          Mês de referência inválido
        </h3>
        <p className="text-color-secondary">
          Por favor, selecione um mês de referência válido no formulário
          anterior.
        </p>
        <Button
          label="Voltar"
          icon="pi pi-arrow-left"
          onClick={onComplete}
          className="mt-3 p-button-secondary"
        />
      </div>
    );
  }

  // Se a data for válida, renderiza o componente principal com toda a lógica.
  return <TimesheetReportUploader date={date} onComplete={onComplete} />;
};

export default TimesheetReportUpload;
